import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { setApkLabel, uploadApk, useApks } from '@/lib/api/apks';
import { BRAND_FAMILIES, BoxBrand } from '@/lib/api/boxes';

export function UploadApkDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [apks] = useApks();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState<BoxBrand | ''>('');
  const [label, setLabel] = useState('');
  // null = not uploading; a number = percent done.
  const [progress, setProgress] = useState<number | null>(null);

  const reset = () => {
    setFile(null);
    setName('');
    setBrand('');
    setLabel('');
    setProgress(null);
  };

  const onUpload = async () => {
    if (!file || !name.trim() || !brand) return;
    setProgress(0);
    try {
      const id = await uploadApk({
        file,
        name: name.trim(),
        brand,
        onProgress: setProgress,
      });
      // Applied after the doc exists so the label sweep (one holder per
      // label) covers freshly uploaded builds too.
      if (label.trim()) {
        const movedFrom = await setApkLabel(id, label, apks);
        toast.success(
          `${name.trim()} uploaded as ${label.trim().toUpperCase()}` +
            (movedFrom ? ` (label moved from ${movedFrom})` : '')
        );
      } else {
        toast.success(`${name.trim()} uploaded`);
      }
      reset();
      onOpenChange(false);
    } catch (error) {
      console.error('apk upload failed', error);
      toast.error('Upload failed');
      setProgress(null);
    }
  };

  const uploading = progress !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (uploading) return; // don't lose an upload to an accidental close
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload APK</DialogTitle>
          <DialogDescription>
            Tag the build with the box type it is for — installs are restricted
            to boxes of that type.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <label className="text-sm font-medium">APK file</label>
            <Input
              type="file"
              accept=".apk"
              disabled={uploading}
              onChange={(event) => {
                const selected = event.target.files?.[0] ?? null;
                setFile(selected);
                // Prefill the display name from the file name once.
                if (selected && !name.trim()) {
                  setName(selected.name.replace(/\.apk$/i, ''));
                }
              }}
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium">Name</label>
            <Input
              value={name}
              disabled={uploading}
              placeholder="e.g. DolphinsTV 2.4.1"
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium">Box type</label>
            <Select
              value={brand}
              disabled={uploading}
              onValueChange={(value) => setBrand(value as BoxBrand)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {BRAND_FAMILIES.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium">Label (optional)</label>
            <Input
              value={label}
              disabled={uploading}
              placeholder="e.g. NV-LATEST / NEXUS-LATEST / SMR-LATEST"
              onChange={(event) => setLabel(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Uppercased on save. A label points at one build — assigning it
              here removes it from any APK that had it.
            </p>
          </div>
          {uploading && (
            <div className="space-y-1">
              <div className="h-2 w-full overflow-hidden rounded bg-secondary">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Uploading… {progress}%
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={onUpload}
            disabled={uploading || !file || !name.trim() || !brand}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
